import { useEffect, useState, useCallback } from 'react';
import { api, ApiError } from '../api/client';
import './InternalToolsPanel.css';

interface InternalToolsStatus {
  total: number;
  eligible: number;
  pushed: number;
}

interface BulkPushResult {
  pushed: number;
  panelIds: string[];
}

interface Props {
  orderId: string;
  /** Polling interval in ms while order isn't fully pushed. Default 10s. */
  pollIntervalMs?: number;
}

/** Order-detail dashboard component for the internal-tools push.
 *  Mirrors OnePwPanel — same counter / bulk button UX — but the bulk
 *  button hits a SYNCHRONOUS route that issues ONE batch API POST
 *  rather than fanning out N queue jobs (the API natively accepts
 *  a batch shape). On success the result fires immediately, no polling
 *  delay; we still poll for the auto-hook case where panels finish
 *  configuring while the operator's looking at the page. */
export function InternalToolsPanel({ orderId, pollIntervalMs = 10_000 }: Props): JSX.Element {
  const [status, setStatus] = useState<InternalToolsStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pushing, setPushing] = useState(false);
  const [pushedFlash, setPushedFlash] = useState<number | null>(null);

  const fetchStatus = useCallback(async (): Promise<InternalToolsStatus | null> => {
    try {
      const data = await api<InternalToolsStatus>(`/api/orders/${orderId}/internal-tools-status`);
      setStatus(data);
      setError(null);
      setLoading(false);
      return data;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load internal-tools status');
      setLoading(false);
      return null;
    }
  }, [orderId]);

  useEffect(() => {
    void fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (!status) return;
    const incomplete = status.pushed < status.eligible || status.eligible < status.total;
    if (!incomplete) return;
    const handle = setInterval(() => void fetchStatus(), pollIntervalMs);
    return () => clearInterval(handle);
  }, [status, fetchStatus, pollIntervalMs]);

  const handleBulkPush = async (): Promise<void> => {
    setPushing(true);
    try {
      const result = await api<BulkPushResult>(
        `/api/orders/${orderId}/internal-tools-push-all-done`,
        { method: 'POST' },
      );
      setPushedFlash(result.pushed);
      // Refresh immediately — the batch is synchronous, counter should already
      // reflect the new state on the next fetch.
      setTimeout(() => void fetchStatus(), 500);
      setTimeout(() => setPushedFlash(null), 6000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Bulk push failed');
    } finally {
      setPushing(false);
    }
  };

  if (loading) return <div className="it-panel it-panel--loading">Loading internal-tools status…</div>;
  if (error) return <div className="it-panel it-panel--error">Internal Tools: {error}</div>;
  if (!status) return <div className="it-panel">No internal-tools status</div>;

  const ineligible = status.total - status.eligible;
  const remaining = status.eligible - status.pushed;
  const allPushed = status.eligible > 0 && status.pushed >= status.eligible;
  const orderComplete = status.eligible === status.total;

  return (
    <div className={`it-panel${allPushed && orderComplete ? ' it-panel--complete' : ''}`}>
      <div className="it-panel__main">
        <div className="it-panel__label">Internal Tools</div>
        <div className="it-panel__count">
          <span className="it-panel__pushed">{status.pushed}</span>
          <span className="it-panel__sep"> / </span>
          <span className="it-panel__eligible">{status.eligible}</span>
          <span className="it-panel__suffix"> pushed</span>
          {ineligible > 0 && (
            <span className="it-panel__ineligible">
              {' '}· {ineligible} not yet eligible
            </span>
          )}
        </div>
        {pushedFlash !== null && (
          <div className="it-panel__flash">
            Pushed {pushedFlash} panel{pushedFlash === 1 ? '' : 's'} in one batch.
          </div>
        )}
      </div>
      {remaining > 0 && (
        <button
          type="button"
          className="it-panel__bulk-btn"
          onClick={() => void handleBulkPush()}
          disabled={pushing}
          title="Send every fully-configured-but-not-yet-pushed panel for this order in one batch API call"
        >
          {pushing ? 'Pushing…' : `Push all done (${remaining})`}
        </button>
      )}
    </div>
  );
}
